import React, { useState, useContext, useEffect, useRef } from 'react';
import { ToastContext } from '../App';

const TODAY = new Date();
TODAY.setHours(0, 0, 0, 0);

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS   = ['Mo','Tu','We','Th','Fr','Sa','Su'];
const TIMES  = ['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00','21:00','22:00'];

function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function parseKey(k) {
  const [y,m,d] = k.split('-').map(Number);
  const dt = new Date(y, m-1, d);
  dt.setHours(0,0,0,0);
  return dt;
}

function calcDays(pickupDate, pickupTime, returnDate, returnTime) {
  if (!pickupDate || !returnDate) return 0;
  const pd = parseKey(pickupDate);
  const rd = parseKey(returnDate);
  const [ph, pm] = pickupTime.split(':').map(Number);
  const [rh, rm] = returnTime.split(':').map(Number);
  pd.setHours(ph, pm, 0, 0);
  rd.setHours(rh, rm, 0, 0);
  const diffMs = rd - pd;
  if (diffMs <= 0) return 0;
  const diffHours = diffMs / 3600000;
  return Math.ceil(diffHours / 24);
}

function getPriceForDate(date, baseprice, seasonalPrices) {
  if (!seasonalPrices || !seasonalPrices.length) return baseprice;
  const month = date.getMonth() + 1;
  const rule = seasonalPrices.find(r => r.month === month);
  return rule ? rule.price : baseprice;
}

function calcTotal(pickupDate, pickupTime, returnDate, returnTime, baseprice, seasonalPrices) {
  const days = calcDays(pickupDate, pickupTime, returnDate, returnTime);
  if (days === 0 || !pickupDate || !returnDate) return 0;
  if (!seasonalPrices || !seasonalPrices.length) return days * baseprice;
  let total = 0;
  const start = parseKey(pickupDate);
  for (let i = 0; i < days; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    total += getPriceForDate(d, baseprice, seasonalPrices);
  }
  return total;
}

// ── MONTH GRID ────────────────────────────────────────
function MonthGrid({ year, month, pickupDate, returnDate, hoverDate, onSelect, onHover }) {
  function getDaysInMonth(y, m) {
    const days = [];
    const firstDay = new Date(y, m, 1);
    let startDow = firstDay.getDay();
    startDow = startDow === 0 ? 6 : startDow - 1;
    for (let i = 0; i < startDow; i++) days.push(null);
    const total = new Date(y, m + 1, 0).getDate();
    for (let d = 1; d <= total; d++) days.push(new Date(y, m, d));
    return days;
  }

  function dayState(date) {
    if (!date) return 'empty';
    const k = dateKey(date);
    const isPast = date < TODAY;
    if (isPast) return 'past';
    if (k === pickupDate) return 'start';
    if (k === returnDate) return 'end';
    if (pickupDate && returnDate) {
      const s = parseKey(pickupDate), e = parseKey(returnDate);
      if (date > s && date < e) return 'between';
    }
    if (pickupDate && !returnDate && hoverDate) {
      const s = parseKey(pickupDate), h = parseKey(hoverDate);
      if (date > s && date < h) return 'hover-between';
      if (k === hoverDate) return 'hover-end';
    }
    return 'normal';
  }

  const days = getDaysInMonth(year, month);

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 15, color: '#111', marginBottom: 12 }}>
        {MONTHS[month]} {year}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0, marginBottom: 4 }}>
        {DAYS.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#9CA3AF', padding: '4px 0' }}>{d}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0 }}>
        {days.map((date, i) => {
          if (!date) return <div key={`e${i}`} style={{ padding: '6px 2px' }}/>;
          const state = dayState(date);
          const isPast = state === 'past';
          const k = dateKey(date);
          const isBetween = state === 'between' || state === 'hover-between';
          const bgColor = state === 'start' || state === 'end' ? '#C41230'
            : state === 'hover-end' ? '#FECACA'
            : isBetween ? '#FEE2E2'
            : 'transparent';
          const textColor = state === 'start' || state === 'end' ? '#fff'
            : state === 'hover-end' || isBetween ? '#C41230'
            : isPast ? '#CBD5E1' : '#111';

          return (
            <div
              key={k}
              onClick={() => !isPast && onSelect(date)}
              onMouseEnter={() => !isPast && onHover(k)}
              onMouseLeave={() => onHover(null)}
              style={{
                padding: '3px 1px',
                background: isBetween ? bgColor : 'transparent',
                borderRadius: isBetween
                  ? (i % 7 === 0 ? '50% 0 0 50%' : i % 7 === 6 ? '0 50% 50% 0' : 0)
                  : 0,
              }}
            >
              <div style={{
                width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto', fontSize: 13,
                fontWeight: state === 'start' || state === 'end' ? 700 : 500,
                cursor: isPast ? 'default' : 'pointer',
                background: !isBetween ? bgColor : 'transparent',
                color: textColor,
                borderRadius: '50%',
                transition: 'all 0.1s',
              }}>
                {date.getDate()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── DESKTOP CALENDAR DROPDOWN ─────────────────────────
function DesktopCalendar({ pickupDate, returnDate, hoverDate, onSelect, onHover }) {
  const [leftMonth, setLeftMonth] = useState(() => ({
    year: TODAY.getFullYear(),
    month: TODAY.getMonth(),
  }));

  const rightMonth = {
    year: leftMonth.month === 11 ? leftMonth.year + 1 : leftMonth.year,
    month: leftMonth.month === 11 ? 0 : leftMonth.month + 1,
  };

  return (
    <div style={{ background: '#fff', borderRadius: '0 0 16px 16px', padding: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', border: '1px solid #F3F4F6', borderTop: 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <button
          onClick={() => setLeftMonth(prev => ({ year: prev.month === 0 ? prev.year - 1 : prev.year, month: prev.month === 0 ? 11 : prev.month - 1 }))}
          style={{ background: '#F3F4F6', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>‹</button>
        <div style={{ flex: 1 }}/>
        <button
          onClick={() => setLeftMonth(prev => ({ year: prev.month === 11 ? prev.year + 1 : prev.year, month: prev.month === 11 ? 0 : prev.month + 1 }))}
          style={{ background: '#F3F4F6', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>›</button>
      </div>
      <div style={{ display: 'flex', gap: 24 }}>
        <MonthGrid year={leftMonth.year} month={leftMonth.month} pickupDate={pickupDate} returnDate={returnDate} hoverDate={hoverDate} onSelect={onSelect} onHover={onHover}/>
        <div style={{ width: 1, background: '#F3F4F6' }}/>
        <MonthGrid year={rightMonth.year} month={rightMonth.month} pickupDate={pickupDate} returnDate={returnDate} hoverDate={hoverDate} onSelect={onSelect} onHover={onHover}/>
      </div>
    </div>
  );
}

// ── MOBILE FULL-SCREEN CALENDAR ───────────────────────
function MobileCalendar({ pickupDate, returnDate, hoverDate, onSelect, onHover, onClose, calendarOpen }) {
  const [startMonth] = useState(() => ({ year: TODAY.getFullYear(), month: TODAY.getMonth() }));

  const months = [];
  for (let i = 0; i < 12; i++) {
    const m = (startMonth.month + i) % 12;
    const y = startMonth.year + Math.floor((startMonth.month + i) / 12);
    months.push({ year: y, month: m });
  }

  function formatDate(d) {
    if (!d) return '—';
    const dt = parseKey(d);
    return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: '#fff', display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{ background: '#C41230', padding: '16px 20px 12px', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#fff' }}>
            {calendarOpen === 'pickup' ? 'Select Pickup Date' : 'Select Return Date'}
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, width: 32, height: 32, color: '#fff', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>
        {/* Date summary */}
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1, background: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: '8px 12px', border: calendarOpen === 'pickup' ? '2px solid #fff' : '2px solid transparent' }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Pickup</div>
            <div style={{ fontSize: 13, color: '#fff', fontWeight: 700 }}>{formatDate(pickupDate)}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', color: 'rgba(255,255,255,0.6)', fontSize: 18 }}>→</div>
          <div style={{ flex: 1, background: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: '8px 12px', border: calendarOpen === 'return' ? '2px solid #fff' : '2px solid transparent' }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Return</div>
            <div style={{ fontSize: 13, color: '#fff', fontWeight: 700 }}>{formatDate(returnDate)}</div>
          </div>
        </div>
      </div>

      {/* Scrollable months */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
        {months.map(({ year, month }, idx) => (
          <div key={idx} style={{ marginBottom: 32 }}>
            <MonthGrid
              year={year} month={month}
              pickupDate={pickupDate} returnDate={returnDate}
              hoverDate={hoverDate} onSelect={onSelect} onHover={onHover}
            />
          </div>
        ))}
      </div>

      {/* Footer button */}
      <div style={{ padding: '12px 20px 28px', borderTop: '1px solid #F3F4F6', flexShrink: 0 }}>
        <button
          onClick={onClose}
          style={{
            width: '100%', background: pickupDate && returnDate ? '#C41230' : '#E5E7EB',
            color: pickupDate && returnDate ? '#fff' : '#9CA3AF',
            border: 'none', borderRadius: 12, padding: '14px',
            fontSize: 15, fontWeight: 700, cursor: pickupDate && returnDate ? 'pointer' : 'default',
          }}>
          {pickupDate && returnDate ? 'Confirm Dates' : pickupDate ? 'Select return date' : 'Select pickup date'}
        </button>
      </div>
    </div>
  );
}

// ── MAIN MODAL ────────────────────────────────────────
export default function BookingModal({ car, onClose, onSuccess }) {
  const showToast = useContext(ToastContext);
  const [step,          setStep]          = useState(1);
  const [loading,       setLoading]       = useState(false);
  const [booking,       setBooking]       = useState(null);
  const [calendarOpen,  setCalendarOpen]  = useState(false);
  const [hoverDate,     setHoverDate]     = useState(null);
  const [isMobile,      setIsMobile]      = useState(window.innerWidth < 700);
  const calRef = useRef(null);

  const [form, setForm] = useState({
    customerName: '', email: '', phone: '',
    pickupDate: '', pickupTime: '10:00',
    returnDate: '', returnTime: '10:00',
  });
  const [errors, setErrors] = useState({});

  const seasonalPrices = car?.seasonalPrices || [];
  const days  = calcDays(form.pickupDate, form.pickupTime, form.returnDate, form.returnTime);
  const total = calcTotal(form.pickupDate, form.pickupTime, form.returnDate, form.returnTime, car?.price || 0, seasonalPrices);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleResize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener('resize', handleResize);
    return () => { document.body.style.overflow = ''; window.removeEventListener('resize', handleResize); };
  }, []);

  // Close desktop calendar on outside click
  useEffect(() => {
    if (isMobile) return;
    function handleClick(e) {
      if (calRef.current && !calRef.current.contains(e.target)) {
        setCalendarOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isMobile]);

  const set = (k, v) => { setForm(f => ({...f, [k]: v})); setErrors(e => ({...e, [k]: ''})); };

  function handleDateSelect(date) {
    const k = dateKey(date);
    if (calendarOpen === 'pickup' || (!form.pickupDate && !form.returnDate)) {
      set('pickupDate', k);
      set('returnDate', '');
      setCalendarOpen('return');
    } else if (calendarOpen === 'return') {
      if (form.pickupDate && parseKey(k) <= parseKey(form.pickupDate)) {
        set('pickupDate', k);
        set('returnDate', '');
        setCalendarOpen('return');
      } else {
        set('returnDate', k);
        if (!isMobile) setCalendarOpen(false);
      }
    }
  }

  const validate = () => {
    const e = {};
    if (!form.customerName.trim()) e.customerName = 'Required';
    if (!form.phone.trim())        e.phone        = 'Required';
    if (!form.email.trim())        e.email        = 'Required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Invalid email';
    if (!form.pickupDate)  e.pickupDate  = 'Required';
    if (!form.returnDate)  e.returnDate  = 'Required';
    else if (days < 1)     e.returnDate  = 'Return must be after pickup';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await fetch('/api/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          carId:        car._id || car.id,
          customerName: form.customerName,
          email:        form.email,
          phone:        form.phone,
          pickupDate:   form.pickupDate,
          pickupTime:   form.pickupTime,
          returnDate:   form.returnDate,
          returnTime:   form.returnTime,
          totalPrice:   total,
        }),
      });
      const data = await res.json();
      if (!res.ok) { showToast(data.error || 'Booking failed', 'error'); setLoading(false); return; }
      setBooking(data);
      setStep(2);
      if (onSuccess) onSuccess();
    } catch {
      showToast('Could not connect to server. Please try again.', 'error');
    }
    setLoading(false);
  };

  function formatDate(d) {
    if (!d) return '';
    const dt = parseKey(d);
    return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  const inputStyle = (err) => ({
    width: '100%', padding: '11px 14px', border: `1.5px solid ${err ? '#DC2626' : '#E5E7EB'}`,
    borderRadius: 10, fontSize: 15, fontFamily: 'inherit', color: '#111827',
    background: err ? '#FFF5F5' : '#FAFAFA', outline: 'none',
    transition: 'border-color 0.15s', boxSizing: 'border-box', cursor: 'pointer',
  });

  const datePickerBtn = (label, value, time, which, err) => (
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>
        {label} <span style={{ color: '#C41230' }}>*</span>
      </div>
      <div
        onClick={() => setCalendarOpen(calendarOpen === which ? false : which)}
        style={{
          ...inputStyle(err),
          display: 'flex', alignItems: 'center', gap: 8,
          border: `1.5px solid ${calendarOpen === which ? '#C41230' : err ? '#DC2626' : '#E5E7EB'}`,
          background: calendarOpen === which ? '#fff' : err ? '#FFF5F5' : '#FAFAFA',
          userSelect: 'none',
        }}
      >
        <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke={value ? '#C41230' : '#9CA3AF'} strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
        </svg>
        <span style={{ flex: 1, color: value ? '#111827' : '#9CA3AF', fontSize: 14, fontWeight: value ? 600 : 400 }}>
          {value ? formatDate(value) : 'Select date'}
        </span>
        <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="#9CA3AF" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/>
        </svg>
      </div>
      {err && <div style={{ color: '#DC2626', fontSize: 11, marginTop: 4 }}>{err}</div>}

      {/* Time selector — always visible */}
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
          {which === 'pickup' ? 'Pickup Time' : 'Return Time'}
        </div>
        <select
          value={time}
          onChange={e => set(which === 'pickup' ? 'pickupTime' : 'returnTime', e.target.value)}
          style={{
            width: '100%', padding: '9px 12px',
            border: '1.5px solid #E5E7EB', borderRadius: 10,
            fontSize: 14, fontFamily: 'inherit', color: '#111827',
            background: '#FAFAFA', outline: 'none', cursor: 'pointer',
          }}
        >
          {TIMES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile full-screen calendar */}
      {isMobile && calendarOpen && (
        <MobileCalendar
          pickupDate={form.pickupDate}
          returnDate={form.returnDate}
          hoverDate={hoverDate}
          onSelect={handleDateSelect}
          onHover={setHoverDate}
          onClose={() => setCalendarOpen(false)}
          calendarOpen={calendarOpen}
        />
      )}

      {/* Main modal overlay */}
      <div
        onClick={e => e.target === e.currentTarget && onClose()}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(6px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      >
        <style>{`
          @keyframes bm_fade { from{opacity:0;transform:scale(0.97)} to{opacity:1;transform:scale(1)} }
          @keyframes bm_up   { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
          .bm-input { width:100%; padding:11px 14px; border:1.5px solid #E5E7EB; border-radius:10px; font-size:15px; font-family:inherit; color:#111827; background:#FAFAFA; outline:none; transition:border-color 0.15s; box-sizing:border-box; }
          .bm-input:focus { border-color:#C41230; background:#fff; }
          .bm-input.err { border-color:#DC2626; background:#FFF5F5; }
          .bm-lbl { display:block; font-size:11px; font-weight:700; color:#6B7280; text-transform:uppercase; letter-spacing:0.07em; margin-bottom:6px; }
          .bm-err { color:#DC2626; font-size:11px; margin-top:4px; }
          .bm-primary { background:#C41230; color:#fff; border:none; border-radius:12px; padding:14px 24px; font-size:14px; font-weight:700; cursor:pointer; font-family:inherit; transition:all 0.15s; }
          .bm-primary:hover:not(:disabled) { background:#A50F27; transform:translateY(-1px); box-shadow:0 6px 20px rgba(196,18,48,0.35); }
          .bm-primary:disabled { opacity:0.6; cursor:not-allowed; }
          .bm-ghost { background:transparent; color:#6B7280; border:1.5px solid #E5E7EB; border-radius:12px; padding:13px 20px; font-size:14px; font-weight:600; cursor:pointer; font-family:inherit; transition:all 0.15s; }
          .bm-ghost:hover { border-color:#C41230; color:#C41230; }
        `}</style>

        <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 520, maxHeight: '94vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,0.35)', animation: 'bm_up 0.22s ease' }}>

          {step === 2 ? (
            <div style={{ padding: '40px 32px', textAlign: 'center', animation: 'bm_up 0.2s ease' }}>
              <div style={{ width: 64, height: 64, background: '#DCFCE7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 28 }}>✅</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#111827', marginBottom: 6 }}>Booking Confirmed!</div>
              <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 28 }}>
                Thank you, <strong>{booking?.customerName?.split(' ')[0]}</strong>. A confirmation email is on its way.
              </div>
              <div style={{ background: '#F9FAFB', border: '1px solid #F3F4F6', borderRadius: 14, padding: '18px 20px', marginBottom: 28, textAlign: 'left' }}>
                {[
                  ['Booking ID', `#${booking?.id || booking?._id}`],
                  ['Vehicle', car?.name],
                  ['Pickup', `${formatDate(form.pickupDate)} at ${form.pickupTime}`],
                  ['Return', `${formatDate(form.returnDate)} at ${form.returnTime}`],
                  ['Duration', `${days} day${days !== 1 ? 's' : ''}`],
                  ['Total', `€${total}`],
                ].map(([l, v]) => (
                  <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #F3F4F6', fontSize: 14 }}>
                    <span style={{ color: '#9CA3AF', fontWeight: 500 }}>{l}</span>
                    <span style={{ fontWeight: 700, color: l === 'Total' ? '#C41230' : '#111827' }}>{v}</span>
                  </div>
                ))}
              </div>
              <button className="bm-primary" style={{ width: '100%' }} onClick={onClose}>Done</button>
            </div>
          ) : (
            <>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 16px', borderBottom: '1px solid #F3F4F6', flexShrink: 0 }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>Reserve Your Car</div>
                  <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>Fill in your details to confirm</div>
                </div>
                <button onClick={onClose}
                  style={{ background: '#F3F4F6', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280', fontSize: 15 }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#FEE2E2'; e.currentTarget.style.color = '#C41230'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#F3F4F6'; e.currentTarget.style.color = '#6B7280'; }}>✕</button>
              </div>

              {/* Car card */}
              <div style={{ margin: '16px 24px 0', background: '#0C0C0C', borderRadius: 14, display: 'flex', alignItems: 'center', gap: 16, padding: '14px 18px', flexShrink: 0 }}>
                {car?.image
                  ? <img src={car.image} alt={car.name} style={{ width: 88, height: 56, objectFit: 'cover', borderRadius: 8, flexShrink: 0, border: '1px solid rgba(255,255,255,0.08)' }}/>
                  : <div style={{ width: 88, height: 56, background: '#1C1C1C', borderRadius: 8, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>🚗</div>
                }
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>{car?.name}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{car?.category}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 24, fontWeight: 900, color: '#C41230', lineHeight: 1 }}>€{car?.price}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontWeight: 600, letterSpacing: '0.06em' }}>/DAY</div>
                </div>
              </div>

              {/* Form */}
              <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>

                {/* Name + Phone */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label className="bm-lbl">Full Name <span style={{ color: '#C41230' }}>*</span></label>
                    <input className={`bm-input${errors.customerName ? ' err' : ''}`} placeholder="John Smith"
                      value={form.customerName} onChange={e => set('customerName', e.target.value)}/>
                    {errors.customerName && <div className="bm-err">{errors.customerName}</div>}
                  </div>
                  <div>
                    <label className="bm-lbl">Phone <span style={{ color: '#C41230' }}>*</span></label>
                    <input className={`bm-input${errors.phone ? ' err' : ''}`} placeholder="+355 69 123 4567"
                      value={form.phone} onChange={e => set('phone', e.target.value)}/>
                    {errors.phone && <div className="bm-err">{errors.phone}</div>}
                  </div>
                </div>

                {/* Email */}
                <div style={{ marginBottom: 16 }}>
                  <label className="bm-lbl">Email <span style={{ color: '#C41230' }}>*</span></label>
                  <input className={`bm-input${errors.email ? ' err' : ''}`} type="email" placeholder="john@example.com"
                    value={form.email} onChange={e => set('email', e.target.value)}/>
                  {errors.email && <div className="bm-err">{errors.email}</div>}
                </div>

                {/* Date pickers with time always visible */}
                <div style={{ display: 'flex', gap: 12, marginBottom: 4, flexDirection: isMobile ? 'column' : 'row' }}>
                  {datePickerBtn('Pickup Date', form.pickupDate, form.pickupTime, 'pickup', errors.pickupDate)}
                  {datePickerBtn('Return Date', form.returnDate, form.returnTime, 'return', errors.returnDate)}
                </div>

                {/* Desktop calendar dropdown */}
                {!isMobile && calendarOpen && (
                  <div ref={calRef} style={{ position: 'relative', zIndex: 100, marginBottom: 12, marginTop: 4 }}>
                    <div style={{ position: 'absolute', left: 0, right: 0, top: 0 }}>
                      <div style={{ background: '#C41230', color: '#fff', fontSize: 12, fontWeight: 700, padding: '8px 16px', borderRadius: '12px 12px 0 0', textAlign: 'center', letterSpacing: '0.04em' }}>
                        {calendarOpen === 'pickup' ? '📅 Select your pickup date' : '📅 Select your return date'}
                      </div>
                      <DesktopCalendar
                        pickupDate={form.pickupDate}
                        returnDate={form.returnDate}
                        hoverDate={hoverDate}
                        onSelect={handleDateSelect}
                        onHover={setHoverDate}
                      />
                    </div>
                    <div style={{ height: 400 }}/>
                  </div>
                )}

                {/* Duration + total summary */}
                {days > 0 && (
                  <div style={{ background: '#0C0C0C', borderRadius: 12, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', animation: 'bm_fade 0.2s ease', marginTop: 8 }}>
                    <div>
                      <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {days} Day{days !== 1 ? 's' : ''} rental
                      </div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 3 }}>
                        {formatDate(form.pickupDate)} {form.pickupTime} → {formatDate(form.returnDate)} {form.returnTime}
                      </div>
                      {seasonalPrices.length > 0 && (
                        <div style={{ fontSize: 10, color: '#C41230', marginTop: 3, fontWeight: 600 }}>Seasonal pricing applied</div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Total</div>
                      <div style={{ fontSize: 28, fontWeight: 900, color: '#C41230', lineHeight: 1.1 }}>€{total}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{ padding: '14px 24px 20px', borderTop: '1px solid #F3F4F6', display: 'flex', gap: 10, flexShrink: 0 }}>
                <button className="bm-ghost" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
                <button className="bm-primary" onClick={handleSubmit} disabled={loading} style={{ flex: 2 }}>
                  {loading ? 'Processing…' : `Confirm Booking${total > 0 ? ` · €${total}` : ''}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}